import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import axios from '../api';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { inputFieldSx, primaryButtonSx, sidebarGradient, primaryGradient } from '../styles/tokens';

const features = [
  { label: 'Inventory', value: 'Real-time tracking', Icon: Inventory2OutlinedIcon, color: '#7f8ce8' },
  { label: 'Bookings', value: 'End-to-end flow', Icon: LocalShippingOutlinedIcon, color: '#60a5fa' },
  { label: 'Analytics', value: 'Business insights', Icon: InsightsOutlinedIcon, color: '#4ade80' },
];

const LoginPage = () => {
  const { login } = useUser();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', form);
      const userData = { username: form.username, role: res.data.role, token: res.data.access_token };
      flushSync(() => login(userData));
      const role = (res.data.role || '').toLowerCase();
      if (role === 'admin' || role === 'accounts') navigate('/');
      else if (role === 'front_office') navigate('/products');
      else if (role === 'warehouse') navigate('/bookings');
      else navigate('/login');
    } catch (err) {
      setError('Invalid credentials. Please check your username and password.');
    } finally {
      setLoading(false);
    }
  };

  const formPanelBg = 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(90, 103, 216, 0.08), transparent 70%), #f8fafc';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Brand panel — desktop */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          px: { lg: 6, xl: 10 },
          background: sidebarGradient,
          color: '#fff',
          minWidth: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative glow orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: -80,
            right: -60,
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(90,103,216,0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -100,
            left: -80,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(96,165,250,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ maxWidth: 460, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              background: primaryGradient,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 8px 32px rgba(90, 103, 216, 0.4)',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1.625rem' }}>E</Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '2.25rem',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              mb: 1.5,
              lineHeight: 1.2,
              color: '#fff',
            }}
          >
            E One Rentals
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: 'rgba(255,255,255,0.65)', mb: 4, lineHeight: 1.65, maxWidth: 400 }}>
            Enterprise inventory and rental management — bookings, quotations, customers, and operations in one place.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {features.map(({ label, value, Icon, color }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  transition: 'background 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.09)' },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: `${color}22`,
                    border: `1px solid ${color}44`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em', color, fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#fff', mt: 0.125 }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Login form panel */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0,
          minHeight: { xs: '100vh', lg: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          background: formPanelBg,
        }}
      >
        {/* Mobile / tablet header */}
        <Box
          sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: 2,
            py: 2.5,
            background: sidebarGradient,
            color: '#fff',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(90,103,216,0.3) 0%, transparent 70%)',
            }}
          />
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              background: primaryGradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.25rem',
              boxShadow: '0 4px 16px rgba(90,103,216,0.4)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            E
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>E One Rentals</Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Enterprise Portal
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
            {/* Welcome header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 3,
                  background: primaryGradient,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: '0 6px 20px rgba(90, 103, 216, 0.35)',
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 26, color: '#fff' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  mb: 0.75,
                  lineHeight: 1.3,
                  color: 'text.primary',
                }}
              >
                Welcome back
              </Typography>
              <Typography sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>
                Sign in to access your dashboard
              </Typography>
            </Box>

            {/* Form card */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'rgba(90, 103, 216, 0.12)',
                bgcolor: 'background.paper',
                boxShadow: '0 8px 32px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(90, 103, 216, 0.06)',
                width: '100%',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: primaryGradient,
                },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.75, letterSpacing: '0.02em' }}>
                Username
              </Typography>
              <TextField
                name="username"
                value={form.username}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                placeholder="Enter your username"
                sx={{ ...inputFieldSx, mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.75, letterSpacing: '0.02em' }}>
                Password
              </Typography>
              <TextField
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                required
                placeholder="Enter your password"
                sx={inputFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{ ...primaryButtonSx, mt: 3, height: 46, fontSize: '0.9375rem' }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: 'white' }} />
                    Signing in...
                  </Box>
                ) : (
                  'Sign in'
                )}
              </Button>
            </Box>

            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: '0.75rem',
                color: 'text.disabled',
              }}
            >
              Secure access for authorized team members only
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
